import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { ApolloQueryResult } from '@apollo/client/core';
import { I18NEXT_SERVICE, I18NextModule, ITranslationService } from 'angular-i18next';
import { BehaviorSubject, debounce, from, interval, map, mergeMap, NEVER, Observable, of, tap } from 'rxjs';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { NotLoggedService } from 'src/app/common/not-logged/not-logged.service';
import { OpenDialogService } from 'src/app/common/open-dialog/open-dialog.service';
import { getDefaultModalSettings } from 'src/app/common/utils/helpers';
import { WonderFragment } from 'src/app/pages/content/content.query.generated';
import { EventFragment } from 'src/app/pages/event/event.query.generated';
import { UserExtended } from 'src/app/pages/profile/profile.component';
import { SharryFragment } from 'src/app/pages/sharry/sharry.query.generated';
import { ContentLike, User } from 'src/types/graphql';
import { BaseComponent } from '../base/base.component';
import {
  GetSharryContentCardBookmarkedGQL,
  GetUserCardBookmarkedGQL,
} from '../content-card/content-card.query.generated';
import { ContentCardService } from '../content-card/content-card.service';
import { UserService } from '../../../common/user/user.service';
import {
  GetContentMyLikeGQL,
  GetContentMyLikeQuery,
  GetProfileMyLikeGQL,
  GetProfileMyLikeQuery,
} from './people-likes.query.generated';

@Component({
  selector: 'sharry-people-likes',
  standalone: true,
  imports: [AsyncPipe, NgTemplateOutlet, I18NextModule, MatButtonModule, IconComponent],
  templateUrl: './people-likes.component.html',
  styleUrls: ['./people-likes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PeopleLikesComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() showFavouriteButton: boolean;
  @Input() showLikeButton: boolean;
  @Input() showShareButton: boolean;
  @Input() content: WonderFragment | EventFragment | SharryFragment | UserExtended;
  @Output() favouriteClicked = new EventEmitter<Partial<User>>();
  myFavourite$: Observable<MyFavourites>;
  myFavouriteSubj: BehaviorSubject<MyFavourites>;
  typeToken: {
    data: MyFavourites;
  };
  myFavouriteId: string;
  user: Partial<User>;
  _content: (WonderFragment | EventFragment | SharryFragment | UserExtended) & {
    _myFavourite: boolean;
    _currentFavourite: boolean;
  };

  favouriteClick$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    public userService: UserService,
    @Inject(I18NEXT_SERVICE) private translateService: ITranslationService,
    private getContentMyLikeGQL: GetContentMyLikeGQL,
    private getUserMyLikeGQL: GetProfileMyLikeGQL,
    private openDialogService: OpenDialogService,
    private notLoggedService: NotLoggedService,
    private contentCardService: ContentCardService,
    private cd: ChangeDetectorRef,
    private getSharryContentCardBookmarkedGQL: GetSharryContentCardBookmarkedGQL,
    private getUserCardBookmarkedGQL: GetUserCardBookmarkedGQL,
  ) {
    super();
    this.myFavouriteSubj = new BehaviorSubject<MyFavourites>({ myFavourite: false, bookmarked: false });
    this.myFavourite$ = this.myFavouriteSubj.asObservable();
  }

  ngOnInit() {
    this.user = this.userService.user;
    this.subscriptions.push(
      this.userService.user$.subscribe(() => {
        if (!!this.userService.user && this.user?.id !== this.userService.user?.id) {
          this.user = this.userService.user;
          this.initButtons();
        }
      }),
    );
    if (this.isPlatformBrowser()) {
      this.subscriptions.push(
        this.favouriteClick$
          .asObservable()
          .pipe(
            tap(() => {
              if (this._content) {
                if (this.userService.user) {
                  this.myFavouriteSubj.next({
                    myFavourite: !this._content._myFavourite,
                    bookmarked: this.myFavouriteSubj.value.bookmarked,
                  });
                  this._content._myFavourite = !this._content._myFavourite;
                  this.favouriteClicked.emit(this._content._myFavourite ? this.userService.user : null);
                  this.cd.detectChanges();
                }
              }
            }),
            debounce(() => (this.userService.user ? interval(1000) : interval(1))),
            mergeMap(v => {
              if (!this.userService.user && v) {
                const fun = () => {
                  this.initButtons(true);
                };
                this.notLoggedService.execute(() => fun());
                return NEVER;
              }
              return of(v);
            }),
          )
          .subscribe(v => {
            if (this.userService.user) {
              if (v && this._content._currentFavourite !== this._content._myFavourite) {
                this._content._currentFavourite = this._content._myFavourite;
                this.cd.detectChanges();
                this.contentCardService.onFavourite(
                  this.content.id,
                  this.content.__typename,
                  this._content._myFavourite,
                );
              }
            }
          }),
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.content?.currentValue) {
      if (changes?.content?.previousValue?.id !== changes?.content?.currentValue?.id) {
        this.initButtons();
      }
    }
  }

  onClick(type: string) {
    switch (type) {
      case 'favourite':
        this.favouriteClick$.next(true);
        break;
      case 'like':
        this.onLike();
        break;
      case 'share':
        this.onShare();
        break;
      default:
        break;
    }
  }

  initButtons(emitSubject = false): void {
    if (this.content.slug) {
      const observable: Observable<
        ApolloQueryResult<GetProfileMyLikeQuery> | ApolloQueryResult<GetContentMyLikeQuery>
      > =
        this.content.__typename === 'User'
          ? this.getUserMyLikeGQL.fetch(
              {
                slug: this.content.slug,
              },
              { fetchPolicy: 'no-cache' },
            )
          : this.getContentMyLikeGQL.fetch(
              {
                slug: this.content.slug,
              },
              { fetchPolicy: 'no-cache' },
            );
      this.subscriptions.push(
        observable
          .pipe(
            map(
              (r: ApolloQueryResult<GetContentMyLikeQuery> | ApolloQueryResult<GetProfileMyLikeQuery>) =>
                (r?.data as GetContentMyLikeQuery)?.sharryContentBySlug ||
                (r?.data as GetProfileMyLikeQuery)?.userBySlug,
            ),
            tap(res => {
              this.myFavouriteSubj.next({ myFavourite: !!res.myFavourite, bookmarked: (res as any).bookmarked });
              this.myFavouriteId = (res as any).myFavourite?.id;
              this.content.myFavourite = res.myFavourite as ContentLike;
              this._content = {
                ...this.content,
                _currentFavourite: !!this.content.myFavourite,
                _myFavourite: !!this.content.myFavourite,
              };
              this.cd.detectChanges();
              if (emitSubject) {
                this.favouriteClick$.next(true);
              }
            }),
          )
          .subscribe(),
      );
    }
  }

  onLike() {
    const fun = () => {
      this.subscriptions.push(
        from(import('../../dialogs/manage-likes/manage-likes.component'))
          .pipe(
            mergeMap(({ ManageLikesComponent }) =>
              this.openDialogService
                .openDialog<any, { content?: any; currentLikeFolderId?: string }>(ManageLikesComponent, {
                  ...getDefaultModalSettings(),
                  data: {
                    content: { ...this.content },
                  },
                })
                .afterClosed(),
            ),
            mergeMap(() => {
              if (this.content.__typename !== 'User') {
                return this.getSharryContentCardBookmarkedGQL
                  .fetch({ id: this.content.id }, { fetchPolicy: 'no-cache' })
                  .pipe(map(r => r.data.sharryContent.bookmarked));
              } else {
                return this.getUserCardBookmarkedGQL
                  .fetch({ id: this.content.id }, { fetchPolicy: 'no-cache' })
                  .pipe(map(r => r.data.user.bookmarked));
              }
            }),
          )
          .subscribe(bookmarked => {
            this.myFavouriteSubj.next({ ...this.myFavouriteSubj.value, bookmarked });
            this.content = { ...this.content, bookmarked } as any;
            this.cd.detectChanges();
          }),
      );
    };
    this.notLoggedService.execute(() => fun());
  }

  onShare() {
    if (!this.content) return;
    const data = {
      data: {
        title:
          this.content.__typename === 'User'
            ? this.translateService.t('people-likes.share-title')
            : this.content?.title,
        content: this.content,
      },
    };
    this.subscriptions.push(
      from(import('src/app/_shared/dialogs/social-sharing-dialog/social-sharing-dialog.component')).subscribe({
        next: ({ SocialSharingDialogComponent }) => {
          this.openDialogService.openDialog(SocialSharingDialogComponent, {
            ...getDefaultModalSettings(),
            height: 'auto',
            ...data,
          });
        },
      }),
    );
  }
}

export interface MyFavourites {
  myFavourite: boolean;
  bookmarked: boolean;
}
